import type { Currency } from './currency.js';

// ─── Payment Mode ───────────────────────────────────────────────────────────

export const PaymentMode = {
  ESCROW: 'ESCROW',
  DIRECT: 'DIRECT',
  OFF_PLATFORM: 'OFF_PLATFORM',
} as const;

export const PaymentModeValues = Object.values(PaymentMode);
export type PaymentMode = (typeof PaymentMode)[keyof typeof PaymentMode];

// ─── Payment Plan ───────────────────────────────────────────────────────────

export const PaymentPlanStatus = {
  ACTIVE: 'ACTIVE',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
} as const;

export const PaymentPlanStatusValues = Object.values(PaymentPlanStatus);
export type PaymentPlanStatus = (typeof PaymentPlanStatus)[keyof typeof PaymentPlanStatus];

// ─── Payment Plan Item ──────────────────────────────────────────────────────

export const PaymentPlanItemType = {
  MILESTONE: 'MILESTONE',
  T_M_PERIOD: 'T_M_PERIOD',
  RETAINER_PERIOD: 'RETAINER_PERIOD',
  SUCCESS_FEE: 'SUCCESS_FEE',
} as const;

export const PaymentPlanItemTypeValues = Object.values(PaymentPlanItemType);
export type PaymentPlanItemType = (typeof PaymentPlanItemType)[keyof typeof PaymentPlanItemType];

export const PaymentPlanItemStatus = {
  PENDING: 'PENDING',
  COMPLETED_PENDING_APPROVAL: 'COMPLETED_PENDING_APPROVAL',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
} as const;

export const PaymentPlanItemStatusValues = Object.values(PaymentPlanItemStatus);
export type PaymentPlanItemStatus = (typeof PaymentPlanItemStatus)[keyof typeof PaymentPlanItemStatus];

// ─── Funding ────────────────────────────────────────────────────────────────

export const FundingStatus = {
  UNFUNDED: 'UNFUNDED',
  PARTIALLY_FUNDED: 'PARTIALLY_FUNDED',
  FULLY_FUNDED: 'FULLY_FUNDED',
  NOT_APPLICABLE: 'NOT_APPLICABLE',
} as const;

export const FundingStatusValues = Object.values(FundingStatus);
export type FundingStatus = (typeof FundingStatus)[keyof typeof FundingStatus];

export const FundingIntentStatus = {
  CREATED: 'CREATED',
  PENDING: 'PENDING',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
  EXPIRED: 'EXPIRED',
  CANCELLED: 'CANCELLED',
} as const;

export const FundingIntentStatusValues = Object.values(FundingIntentStatus);
export type FundingIntentStatus = (typeof FundingIntentStatus)[keyof typeof FundingIntentStatus];

export const PaymentMethod = {
  CARD: 'CARD',
  BANK_TRANSFER: 'BANK_TRANSFER',
} as const;

export const PaymentMethodValues = Object.values(PaymentMethod);
export type PaymentMethod = (typeof PaymentMethod)[keyof typeof PaymentMethod];

// ─── Payout ─────────────────────────────────────────────────────────────────

export const PayoutStatus = {
  NOT_DUE: 'NOT_DUE',
  READY_FOR_PAYOUT: 'READY_FOR_PAYOUT',
  PARTIALLY_PAID: 'PARTIALLY_PAID',
  PAID: 'PAID',
  BLOCKED: 'BLOCKED',
} as const;

export const PayoutStatusValues = Object.values(PayoutStatus);
export type PayoutStatus = (typeof PayoutStatus)[keyof typeof PayoutStatus];

export const PayoutItemStatus = {
  PLANNED: 'PLANNED',
  /** Removed from batch before export; underlying plan item stays READY_FOR_PAYOUT. */
  EXCLUDED: 'EXCLUDED',
  SENT_TO_PSP: 'SENT_TO_PSP',
  PAID_OUT: 'PAID_OUT',
  FAILED: 'FAILED',
} as const;

export const PayoutItemStatusValues = Object.values(PayoutItemStatus);
export type PayoutItemStatus = (typeof PayoutItemStatus)[keyof typeof PayoutItemStatus];

export const PayoutBatchStatus = {
  /** Legacy / ops: batch created and sent to PSP immediately. */
  PLANNED: 'PLANNED',
  PROCESSING: 'PROCESSING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  /** Client org workflow: draft → approvals → ABO export (no PSP in draft path). */
  DRAFT: 'DRAFT',
  PENDING_FIRST_APPROVAL: 'PENDING_FIRST_APPROVAL',
  PENDING_SECOND_APPROVAL: 'PENDING_SECOND_APPROVAL',
  APPROVED: 'APPROVED',
  ABO_EXPORTED: 'ABO_EXPORTED',
  CANCELLED: 'CANCELLED',
} as const;

export const PayoutBatchStatusValues = Object.values(PayoutBatchStatus);
export type PayoutBatchStatus = (typeof PayoutBatchStatus)[keyof typeof PayoutBatchStatus];

export const PayeeType = {
  CONSULTANT: 'CONSULTANT',
  PROVIDER_ORG: 'PROVIDER_ORG',
  CLIENT_ORG: 'CLIENT_ORG',
} as const;

export const PayeeTypeValues = Object.values(PayeeType);
export type PayeeType = (typeof PayeeType)[keyof typeof PayeeType];

// ─── Escrow ─────────────────────────────────────────────────────────────────

export const EscrowAccountStatus = {
  OPEN: 'OPEN',
  FROZEN: 'FROZEN',
  CLOSED: 'CLOSED',
} as const;

export const EscrowAccountStatusValues = Object.values(EscrowAccountStatus);
export type EscrowAccountStatus = (typeof EscrowAccountStatus)[keyof typeof EscrowAccountStatus];

/** Present when `EscrowAccountStatus` is `FROZEN`. */
export const EscrowFrozenReason = {
  DISPUTE: 'DISPUTE',
  MANUAL: 'MANUAL',
  CHARGEBACK: 'CHARGEBACK',
} as const;

export const EscrowFrozenReasonValues = Object.values(EscrowFrozenReason);
export type EscrowFrozenReason = (typeof EscrowFrozenReason)[keyof typeof EscrowFrozenReason];

export const EscrowTransactionType = {
  FUNDING: 'FUNDING',
  RELEASE: 'RELEASE',
  REFUND: 'REFUND',
  FEE: 'FEE',
} as const;

export const EscrowTransactionTypeValues = Object.values(EscrowTransactionType);
export type EscrowTransactionType = (typeof EscrowTransactionType)[keyof typeof EscrowTransactionType];

export const EscrowTransactionDirection = {
  CREDIT: 'CREDIT',
  DEBIT: 'DEBIT',
} as const;

export const EscrowTransactionDirectionValues = Object.values(EscrowTransactionDirection);
export type EscrowTransactionDirection = (typeof EscrowTransactionDirection)[keyof typeof EscrowTransactionDirection];

export const EscrowTransactionStatus = {
  PENDING: 'PENDING',
  POSTED: 'POSTED',
  FAILED: 'FAILED',
} as const;

export const EscrowTransactionStatusValues = Object.values(EscrowTransactionStatus);
export type EscrowTransactionStatus = (typeof EscrowTransactionStatus)[keyof typeof EscrowTransactionStatus];

// ─── Dispute ────────────────────────────────────────────────────────────────

export const DisputeStatus = {
  OPEN: 'OPEN',
  UNDER_REVIEW: 'UNDER_REVIEW',
  RESOLVED_CLIENT_FAVOUR: 'RESOLVED_CLIENT_FAVOUR',
  RESOLVED_PROVIDER_FAVOUR: 'RESOLVED_PROVIDER_FAVOUR',
  COMPROMISE: 'COMPROMISE',
  CANCELLED: 'CANCELLED',
} as const;

export const DisputeStatusValues = Object.values(DisputeStatus);
export type DisputeStatus = (typeof DisputeStatus)[keyof typeof DisputeStatus];

export const DisputeReasonCode = {
  QUALITY_ISSUE: 'QUALITY_ISSUE',
  SCOPE_DISAGREEMENT: 'SCOPE_DISAGREEMENT',
  DELAY: 'DELAY',
  BILLING_DISAGREEMENT: 'BILLING_DISAGREEMENT',
  CANCELLATION: 'CANCELLATION',
  OVERPAYMENT: 'OVERPAYMENT',
  CHARGEBACK: 'CHARGEBACK',
  OTHER: 'OTHER',
} as const;

export const DisputeReasonCodeValues = Object.values(DisputeReasonCode);
export type DisputeReasonCode = (typeof DisputeReasonCode)[keyof typeof DisputeReasonCode];

// ─── Refund request (escrow client refunds) ───────────────────────────────────

export const RefundRequestCategory = {
  PRE_WORK: 'PRE_WORK',
  PARTIAL: 'PARTIAL',
} as const;

export const RefundRequestCategoryValues = Object.values(RefundRequestCategory);
export type RefundRequestCategory = (typeof RefundRequestCategory)[keyof typeof RefundRequestCategory];

export const RefundRequestStatus = {
  SUBMITTED: 'SUBMITTED',
  APPROVED: 'APPROVED',
  PROCESSING: 'PROCESSING',
  COMPLETED: 'COMPLETED',
  REJECTED: 'REJECTED',
  FAILED: 'FAILED',
} as const;

export const RefundRequestStatusValues = Object.values(RefundRequestStatus);
export type RefundRequestStatus = (typeof RefundRequestStatus)[keyof typeof RefundRequestStatus];

// ─── Invoice ────────────────────────────────────────────────────────────────

export const InvoiceType = {
  CLIENT_INVOICE: 'CLIENT_INVOICE',
  PAYOUT_STATEMENT: 'PAYOUT_STATEMENT',
  CREDIT_NOTE: 'CREDIT_NOTE',
} as const;

export const InvoiceTypeValues = Object.values(InvoiceType);
export type InvoiceType = (typeof InvoiceType)[keyof typeof InvoiceType];

export const InvoiceStatus = {
  DRAFT: 'DRAFT',
  DUE: 'DUE',
  PAID: 'PAID',
  PARTIALLY_PAID: 'PARTIALLY_PAID',
  OVERDUE: 'OVERDUE',
  UNDER_DISPUTE: 'UNDER_DISPUTE',
  CANCELLED: 'CANCELLED',
} as const;

export const InvoiceStatusValues = Object.values(InvoiceStatus);
export type InvoiceStatus = (typeof InvoiceStatus)[keyof typeof InvoiceStatus];

// ─── Payment Confirmation (DIRECT mode) ─────────────────────────────────────

export const PaymentConfirmationStatus = {
  PENDING: 'PENDING',
  CONFIRMED: 'CONFIRMED',
  REJECTED: 'REJECTED',
} as const;

export const PaymentConfirmationStatusValues = Object.values(PaymentConfirmationStatus);
export type PaymentConfirmationStatus = (typeof PaymentConfirmationStatus)[keyof typeof PaymentConfirmationStatus];

export const OffPlatformPaymentStatus = {
  PENDING: 'PENDING',
  CONFIRMED: 'CONFIRMED',
  REJECTED: 'REJECTED',
} as const;

export const OffPlatformPaymentStatusValues = Object.values(OffPlatformPaymentStatus);
export type OffPlatformPaymentStatus =
  (typeof OffPlatformPaymentStatus)[keyof typeof OffPlatformPaymentStatus];

// ─── Provider User Mapping ──────────────────────────────────────────────────

export const KycStatus = {
  NOT_STARTED: 'NOT_STARTED',
  PENDING: 'PENDING',
  VALIDATED: 'VALIDATED',
  REFUSED: 'REFUSED',
} as const;

export const KycStatusValues = Object.values(KycStatus);
export type KycStatus = (typeof KycStatus)[keyof typeof KycStatus];

// ─── SPAYD (QR Payment) ─────────────────────────────────────────────────────

export interface SpaydPaymentDataDto {
  spaydString: string;
  iban: string;
  amount: number;
  currency: string;
  variableSymbol?: string;
  recipientName?: string;
  message?: string;
}

// ─── DTOs ───────────────────────────────────────────────────────────────────

/** VAT-inclusive gross → net / VAT parts; `vatRate` is decimal (e.g. 0.21). */
export function roundMoney2(amount: number): number {
  return Math.round(amount * 100) / 100;
}

export function computePaymentPlanItemAmountBreakdown(params: {
  amountGross: number;
  totalAmountGross: number;
  vatRate: number;
  platformFeeRate: number;
  platformFixedFee: number;
}): { amountNet: number; vatAmount: number; platformFeeAmount: number } {
  const { amountGross, totalAmountGross, vatRate, platformFeeRate, platformFixedFee } = params;
  const divisor = 1 + (Number.isFinite(vatRate) ? vatRate : 0);
  const amountNet = divisor > 0 ? roundMoney2(amountGross / divisor) : roundMoney2(amountGross);
  const vatAmount = roundMoney2(amountGross - amountNet);
  const share = totalAmountGross > 0 ? amountGross / totalAmountGross : 0;
  const platformFeeAmount = roundMoney2(amountGross * platformFeeRate + platformFixedFee * share);
  return { amountNet, vatAmount, platformFeeAmount };
}

export interface PaymentPlanDto {
  id: string;
  contractId: string;
  projectId: string;
  pricingModel: string;
  defaultPaymentMode: PaymentMode;
  currency: Currency;
  totalAmountGross: number;
  /** Decimal VAT rate (e.g. 0.21) for splitting gross into net + VAT. */
  vatRate: number;
  platformFeeRate: number;
  platformFixedFee: number;
  status: PaymentPlanStatus;
  escrowAccountId?: string;
  /** Present when an escrow account is linked to the plan. */
  escrowAccount?: Pick<EscrowAccountDto, 'id' | 'balanceTotal' | 'currency' | 'status' | 'frozenReason'>;
  items: PaymentPlanItemDto[];
  createdAt: string;
  updatedAt: string;
}

export interface PaymentPlanItemDto {
  id: string;
  paymentPlanId: string;
  type: PaymentPlanItemType;
  paymentMode: PaymentMode;
  label: string;
  orderIndex: number;
  amountGross: number;
  /** Net amount when gross is VAT-inclusive at plan `vatRate`. */
  amountNet: number;
  /** VAT portion when gross is VAT-inclusive at plan `vatRate`. */
  vatAmount: number;
  /** Platform fee share for this line (rate × gross + proportional fixed fee). */
  platformFeeAmount: number;
  escrowTargetAmount: number;
  fundingStatus: FundingStatus;
  payoutStatus: PayoutStatus;
  status: PaymentPlanItemStatus;
  dueDate?: string;
  projectMilestoneId?: number;
  spaydData?: SpaydPaymentDataDto;
  /** Populated when loading a project payment plan (direct mode confirmations for this line). */
  paymentConfirmations?: PaymentConfirmationDto[];
  /** Declarations for OFF_PLATFORM lines (consultant report + approval workflow). */
  offPlatformPayments?: OffPlatformPaymentDto[];
  /** All disputes for this plan line (newest first), when batch-loaded with the project payment plan. */
  disputes?: DisputeDto[];
  createdAt: string;
  updatedAt: string;
}

/** Minimal shape for {@link pickFirstEscrowItemIdNeedingFunding} (DTO or ORM entity). */
export type EscrowFundingPickItem = {
  id: string;
  orderIndex: number;
  paymentMode: string;
  fundingStatus: string;
};

/** First ESCROW line by orderIndex that still needs funding; every prior ESCROW line must be FULLY_FUNDED. */
export function pickFirstEscrowItemIdNeedingFunding(items: EscrowFundingPickItem[]): string | null {
  const sorted = [...items].sort((a, b) => a.orderIndex - b.orderIndex);
  for (const item of sorted) {
    if (item.paymentMode !== PaymentMode.ESCROW) {
      continue;
    }
    if (item.fundingStatus !== FundingStatus.UNFUNDED && item.fundingStatus !== FundingStatus.PARTIALLY_FUNDED) {
      continue;
    }
    const priorEscrowBlocks = sorted.some(
      (i) =>
        i.orderIndex < item.orderIndex &&
        i.paymentMode === PaymentMode.ESCROW &&
        i.fundingStatus !== FundingStatus.FULLY_FUNDED,
    );
    if (priorEscrowBlocks) {
      continue;
    }
    return item.id;
  }
  return null;
}

/** Minimal shape for {@link pickFirstDirectItemIdAwaitingConfirmedReceipt}. */
export type DirectPaymentPlanItemPick = {
  id: string;
  orderIndex: number;
  paymentMode: string;
};

export type DirectPaymentConfirmationPick = {
  paymentPlanItemId: string;
  status: string;
};

function directItemHasConfirmedReceipt(
  itemId: string,
  confirmations: DirectPaymentConfirmationPick[],
): boolean {
  return confirmations.some(
    (c) => c.paymentPlanItemId === itemId && c.status === PaymentConfirmationStatus.CONFIRMED,
  );
}

/**
 * First DIRECT line by orderIndex that does not yet have a CONFIRMED receipt; every prior DIRECT line
 * must already have a CONFIRMED payment confirmation.
 */
export function pickFirstDirectItemIdAwaitingConfirmedReceipt(
  items: DirectPaymentPlanItemPick[],
  confirmations: DirectPaymentConfirmationPick[],
): string | null {
  const sorted = [...items].sort((a, b) => a.orderIndex - b.orderIndex);
  for (const item of sorted) {
    if (item.paymentMode !== PaymentMode.DIRECT) {
      continue;
    }
    const priorDirectBlocks = sorted.some(
      (i) =>
        i.orderIndex < item.orderIndex &&
        i.paymentMode === PaymentMode.DIRECT &&
        !directItemHasConfirmedReceipt(i.id, confirmations),
    );
    if (priorDirectBlocks) {
      continue;
    }
    if (directItemHasConfirmedReceipt(item.id, confirmations)) {
      continue;
    }
    return item.id;
  }
  return null;
}

/** Prefix stored in {@link PaymentConfirmationDto.documentUrl} for project workspace proof files. */
export const PAYMENT_PROOF_DOCUMENT_URL_PREFIX = 'workspace-file:' as const;

export function buildPaymentProofDocumentUrl(workspaceFileId: string): string {
  return `${PAYMENT_PROOF_DOCUMENT_URL_PREFIX}${workspaceFileId}`;
}

export function parsePaymentProofWorkspaceFileId(documentUrl: string | null | undefined): string | null {
  if (!documentUrl?.startsWith(PAYMENT_PROOF_DOCUMENT_URL_PREFIX)) {
    return null;
  }
  const id = documentUrl.slice(PAYMENT_PROOF_DOCUMENT_URL_PREFIX.length).trim();
  return id.length > 0 ? id : null;
}

export interface EscrowAccountDto {
  id: string;
  contractId: string;
  currency: Currency;
  balanceTotal: number;
  status: EscrowAccountStatus;
  frozenReason?: EscrowFrozenReason | null;
  createdAt: string;
}

/** Client-facing row for escrow freeze UI (one row per payment plan with escrow). */
export interface ClientEscrowPlanSummaryDto {
  paymentPlanId: string;
  projectId: string;
  projectTitle: string;
  currency: Currency;
  escrowAccountId: string;
  escrowStatus: EscrowAccountStatus;
  frozenReason: EscrowFrozenReason | null;
}

export const PaymentAuditEventType = {
  FundingIntentStatusChanged: 'payment-funding-intent-status-changed',
  EscrowAccountStatusChanged: 'payment-escrow-account-status-changed',
  MilestoneReleased: 'payment-milestone-released',
  PayoutBatchApprovalStep: 'payment-payout-batch-approval-step',
  ChargebackOpened: 'payment-chargeback-opened',
  ChargebackClosed: 'payment-chargeback-closed',
  EscrowManualFreeze: 'payment-escrow-manual-freeze',
  EscrowManualUnfreeze: 'payment-escrow-manual-unfreeze',
} as const;

export type PaymentAuditEventType = (typeof PaymentAuditEventType)[keyof typeof PaymentAuditEventType];

export const PaymentAuditEventTypeValues = Object.values(PaymentAuditEventType);

export interface PaymentAuditEventDto {
  id: string;
  eventType: PaymentAuditEventType;
  createdAt: string;
  detail: string | null;
  metadata: Record<string, unknown> | null;
  actorUserId: string | null;
  actorEmail: string | null;
}

export interface PaymentAuditListResponseDto {
  items: PaymentAuditEventDto[];
  nextCursor: string | null;
}

export interface EscrowTransactionDto {
  id: string;
  escrowAccountId: string;
  paymentPlanItemId?: string;
  type: EscrowTransactionType;
  direction: EscrowTransactionDirection;
  amount: number;
  status: EscrowTransactionStatus;
  createdAt: string;
}

export interface FundingIntentDto {
  id: string;
  paymentPlanId: string;
  paymentMethod: PaymentMethod;
  amountTotal: number;
  status: FundingIntentStatus;
  redirectUrl?: string;
  bankTransferInstructions?: BankTransferInstructionsDto;
  items: FundingIntentItemDto[];
  createdAt: string;
}

export interface FundingIntentItemDto {
  id: string;
  paymentPlanItemId: string;
  amount: number;
}

export interface BankTransferInstructionsDto {
  iban: string;
  bic: string;
  reference: string;
  amount: number;
  currency: Currency;
}

export interface PayoutItemDto {
  id: string;
  payoutBatchId: string;
  paymentPlanItemId: string;
  payeeType: PayeeType;
  /** Project id used as payout grouping key for consultant projects. */
  payeeOrgId: string;
  /** Net amount sent to PSP (same as aggregateAmountNet when snapshots exist). */
  amount: number;
  /** Sum of line gross amounts for this payout row (snapshot at batch time). */
  amountGross: number;
  platformFeeAmount: number;
  amountNet: number;
  currency: Currency;
  projectId: string;
  projectTitle: string;
  status: PayoutItemStatus;
  paidAt?: string;
  createdAt: string;
  /** Supplier account receiving payout (offer.accountId); set for client-batch review when known. */
  supplierAccountId?: string;
  creditorIban?: string;
  creditorName?: string;
}

export const PayoutBatchApprovalStep = {
  FIRST: 1,
  SECOND: 2,
} as const;

export type PayoutBatchApprovalStep =
  (typeof PayoutBatchApprovalStep)[keyof typeof PayoutBatchApprovalStep];

export interface PayoutBatchApprovalDto {
  step: PayoutBatchApprovalStep;
  userId: string;
  createdAt: string;
}

export interface PayoutBatchDto {
  id: string;
  currency: Currency;
  status: PayoutBatchStatus;
  /** Client (inquirer) org when batch is account-scoped; null for legacy global PSP batches. */
  clientAccountId?: string | null;
  scheduledFor?: string;
  items: PayoutItemDto[];
  includedItemCount: number;
  excludedItemCount: number;
  totalNetIncluded: number;
  totalNetExcluded: number;
  submittedAt?: string;
  submittedByUserId?: string;
  aboExportedAt?: string;
  approvals: PayoutBatchApprovalDto[];
  createdAt: string;
}

/** Body: POST /client/payout-batches */
export interface CreateClientPayoutBatchDto {
  currency: Currency;
  /** If omitted or empty, all READY_FOR_PAYOUT items for the client account and currency are included. */
  paymentPlanItemIds?: string[];
}

/** Body: PATCH /client/payout-batches/:id/items */
export interface UpdateClientPayoutBatchItemsDto {
  /** Payout line ids (poi_*) to mark excluded; lines not listed are included (PLANNED). */
  excludedPayoutItemIds: string[];
}

export interface DisputeDto {
  id: string;
  paymentPlanItemId: string;
  /** Present when loaded with payment plan relations (e.g. admin list, project plans). */
  projectId?: string;
  invoiceId?: string;
  reasonCode: DisputeReasonCode;
  status: DisputeStatus;
  description: string;
  disputedAmount: number;
  /** Workspace proof references (`workspace-file:{id}`) supplied when the dispute was opened. */
  attachmentUrls?: string[];
  refundAmount?: number;
  releaseAmount?: number;
  resolutionSummary?: string;
  createdAt: string;
  updatedAt: string;
}

export interface RefundRequestDto {
  id: string;
  projectId: string;
  paymentPlanItemId: string;
  /** Plan currency when relations are loaded (client list, OPS list). */
  currency?: Currency;
  requestedByUserId: string;
  category: RefundRequestCategory;
  requestedAmount: number;
  reason: string;
  status: RefundRequestStatus;
  resolutionNotes?: string;
  resolvedByUserId?: string;
  creditNoteInvoiceId?: string;
  escrowTransactionId?: string;
  /** MangoPay PayIn used for the card/bank refund (single pay-in allocation). */
  sourceProviderPayInId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceDto {
  id: string;
  paymentPlanItemId: string;
  type: InvoiceType;
  invoiceNumber: string;
  amount: number;
  currency: Currency;
  status: InvoiceStatus;
  dueDate?: string;
  pdfUrl?: string;
  /** When type is CREDIT_NOTE, optional link to the adjusted client invoice. */
  referencesInvoiceId?: string;
  createdAt: string;
}

export interface PaymentConfirmationDto {
  id: string;
  paymentPlanItemId: string;
  amount: number;
  currency: Currency;
  paidAt: string;
  /** Proof reference; `workspace-file:{id}` when tied to a project workspace upload. */
  documentUrl?: string;
  reportedByUserId: string;
  receivedByUserId?: string;
  receivedAt?: string;
  status: PaymentConfirmationStatus;
  createdAt: string;
}

export interface OffPlatformPaymentDto {
  id: string;
  paymentPlanItemId: string;
  amount: number;
  currency: Currency;
  paidAt: string;
  /** Proof reference; `workspace-file:{id}` when tied to a project workspace upload. */
  documentUrl?: string;
  reportedByUserId: string;
  status: OffPlatformPaymentStatus;
  reviewedByUserId?: string;
  reviewedAt?: string;
  rejectionReason?: string;
  note?: string;
  createdAt: string;
}

// ─── Request DTOs ───────────────────────────────────────────────────────────

/**
 * POST /payment-plans/:id/funding-intents - vytvoří jeden funding intent v kolekci `funding-intents`.
 * Když `paymentPlanItemIds` chybí nebo je prázdné, server vybere další řádek plánu, který je ESCROW
 * a potřebuje dofinancovat (viz {@link pickFirstEscrowItemIdNeedingFunding}).
 */
export interface CreateFundingIntentDto {
  /** Pokud vynecháno nebo `[]`, použije se automatický výběr další escrow položky. */
  paymentPlanItemIds?: string[];
  paymentMethod: PaymentMethod;
  returnUrl?: string;
}

export interface RaiseDisputeDto {
  reasonCode: DisputeReasonCode;
  description: string;
  disputedAmount: number;
  attachmentUrls?: string[];
  /** When disputing a T&M invoice, links the dispute to the invoice row. */
  invoiceId?: string;
}

export interface ApproveInvoiceForPaymentDto {
  note?: string;
}

export interface ResolveDisputeDto {
  status: typeof DisputeStatus.RESOLVED_CLIENT_FAVOUR | typeof DisputeStatus.RESOLVED_PROVIDER_FAVOUR | typeof DisputeStatus.COMPROMISE;
  refundAmount?: number;
  releaseAmount?: number;
  resolutionSummary: string;
}

export interface CreateRefundRequestDto {
  paymentPlanItemId: string;
  category: RefundRequestCategory;
  requestedAmount: number;
  reason: string;
}

export interface RejectRefundRequestDto {
  resolutionNotes: string;
}

export interface ConfirmDirectPaymentDto {
  amount: number;
  currency: Currency;
  paidAt: string;
  /** Project workspace file id (PDF proof uploaded to this project). */
  workspaceFileId: string;
}

export interface DeclareOffPlatformPaymentDto {
  amount: number;
  currency: Currency;
  paidAt: string;
  /** Project workspace file id (PDF proof uploaded to this project). */
  workspaceFileId: string;
  note?: string;
}

export interface RejectOffPlatformPaymentDto {
  reason?: string;
}

export interface ApprovePaymentPlanItemDto {
  note?: string;
}

export interface RequestChangesDto {
  reason: string;
}
